import React, { useState, useMemo, useEffect } from "react";
import {
  X,
  ChevronsLeft,
  ChevronRight,
} from "lucide-react";

import { sendRequest } from "../../api";
import { useUserContext } from "../UserProvider";

import UserProvider from "../UserProvider";
import UpgradePlanProvider from "./UpgradePlanProvider";

import Navbar from "./navbar"
import LeftSidebar from "./LeftSidebar";
import AudioPlayer from "./AudioPlayer";
import Onboarding, { getIsOnboarding } from "./onboarding"
import ProgressBar from "./ProgressBar";
import UserSummary from "./UserSummary";
import Publisher from "./Publisher";
import Error from "../Error";
import Newsletter from "./Newsletter";
import RightSidebar from "./RightSidebar";

import "./home.css";
import { useParams } from "react-router-dom";
import { Routes, Route } from "react-router-dom";

const HomeWrapper = () => (
  <UserProvider>
    <UpgradePlanProvider>
      <Home />
    </UpgradePlanProvider>
  </UserProvider>
);

const Home = ({ children }) => {
  const { publisherName, categoryName } = useParams();

  const {
    counts, userData, reloadUser, isLoading,
    setIsLoading, loadingProgress, userSummaries,
    isDisconnected, setIsDisconnected, errorMessage,
  } = useUserContext();

  const [isDrawerOpen, setIsDrawerOpen] = useState(() => window.innerWidth >= 768);
  const [timeSavedOpen, setTimeSavedOpen] = useState(false)
  const [audio, setAudio] = useState({})
  const [isOnboarding, setIsOnboarding] = useState(getIsOnboarding())
  const [summaries, setSummaries] = useState([])
  const [annotations, setAnnotations] = useState(null)
  const [loadingAnnotations, setLoadingAnnotations] = useState(false)
  const [isContextDrawerOpen, setIsContextDrawerOpen] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsDrawerOpen(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const run = async () => {
      if (categoryName) {
        const summaries = await sendRequest(`/categories/${categoryName}`)
        setSummaries(summaries)
      }
      if (publisherName) {
        const summaries = await sendRequest(`/publishers/${publisherName}`)
        setSummaries(summaries)
      }
    }
    run()
  }, [categoryName, publisherName])

  const loadingTitle = () => {
    if (loadingProgress < 6) {
      return "Generating summary: warming up..."
    }
    if (loadingProgress < 20) {
      return "Generating summary: loading newsletters..."
    }
    if (loadingProgress < 80) {
      return "Generating summary: summarizing articles..."
    }
    if (loadingProgress < 90) {
      return "Almost there..."
    }
  }

  const handleGenerateSummary = async () => {
    setIsLoading(true);
    try {
      await sendRequest("/user_summaries/generate", "POST");
      await reloadUser();
    } catch (error) {
      console.error("Error initiating summary generation:", error);
      setIsLoading(false);
    }
  };

  const toggleContextDrawer = () => {
    // Disabled for now
    // if (!isContextDrawerOpen) {
    //   setIsContextDrawerOpen(true);
    // }
  };

  const toggleSection = (section) => {
    switch (section) {
      case "timeSaved":
        setTimeSavedOpen(!timeSavedOpen);
        break;
      case "publishers":
        break;
      default:
        console.warn(`Unknown section: ${section}`);
    }
  };

  const handleViewAnnotations = async (userSummaryId) => {
    setLoadingAnnotations(true);
    // Enable context drawer to show annotations
    setIsContextDrawerOpen(true);
    
    try {
      const response = await sendRequest(`/annotations/${userSummaryId}`);
      setAnnotations(response.annotations);
    } catch (error) {
      console.error("Error fetching annotations:", error);
      setAnnotations([]);
    } finally {
      setLoadingAnnotations(false);
    }
  };

  const handleCloseAnnotations = () => {
    // Commented out to prevent closing
    // setAnnotations(null);
  };

  const renderMainContent = () => {
    if (publisherName) {
      return <Publisher slug={publisherName} />
    }

    const { newsletterId } = useParams();
    if (newsletterId) {
      return <Newsletter newsletterId={newsletterId} />
    }

    return userData &&
      userSummaries &&
      userSummaries.length > 0
      ? <UserSummary 
          userSummaries={userSummaries} 
          audio={audio} 
          setAudio={setAudio} 
          onViewAnnotations={handleViewAnnotations}
        />
      : null
  }

  const confirmPlanUpgrade = async () => {
    await sendRequest("/payments/confirm", "POST");
    await reloadUser();
  }
  
  return (
    <div className={`flex flex-col h-screen bg-white text-gray-800 ${audio.url ? 'audio-player-active' : ''}`}>
      <header className="flex justify-between items-center p-4 border-b border-gray-200">
        <Navbar 
          userData={userData} 
          isDrawerOpen={isDrawerOpen} 
          setIsDrawerOpen={setIsDrawerOpen} 
          isContextDrawerOpen={isContextDrawerOpen}
          setIsContextDrawerOpen={setIsContextDrawerOpen}
        />
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside
          className={`border-r border-gray-200 overflow-scroll transition-all duration-300 ease-in-out transform flex flex-col 
            md:relative md:drawer-desktop 
            ${isDrawerOpen 
              ? `opacity-100 drawer-open translate-x-0 fixed ${window.innerWidth < 768 ? "top-[57px]" : "top-0"} bottom-0 left-0 z-50 bg-white` 
              : "opacity-0 w-0 -translate-x-full"}`}
        >
          <LeftSidebar
            toggleSection={toggleSection}
            userData={userData}
            reloadUser={reloadUser}
            counts={counts}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isOnboarding={isOnboarding}
            handleGenerateSummary={handleGenerateSummary}
            audio={audio}
          />
        </aside>

        <button className="rounded-l-md p-2 transition-all duration-300 ease-in-out hidden md:block" onClick={() => setIsDrawerOpen(!isDrawerOpen)}>
          <ChevronsLeft 
            size={20} 
            className={`transform transition-transform duration-300 ${!isDrawerOpen ? "rotate-180" : ""}`}
          />
        </button>
        {isDrawerOpen && (
          <div 
            className={`md:hidden fixed inset-0 ${window.innerWidth < 768 ? "top-[57px]" : "top-0"} bg-black bg-opacity-50 z-40`}
            onClick={() => setIsDrawerOpen(false)}
          />
        )}

        {/* Center content */}
        <main
          className={`w-full flex justify-center px-2 transition-all duration-300 mx-4`}
        >
          <div className="flex flex-col grow overflow-auto">
            {isDisconnected && (
              <div className="mb-4 bg-red-100 rounded p-4 text-sm flex items-center">
                <X className="text-red-500 cursor-pointer mr-2" size={16} onClick={() => setIsDisconnected(false)} />
                <span className="text-red-700">Connection to server lost. It may come back on its own or refresh the page to reconnect.</span>
              </div>
            )}
            {errorMessage && <Error message={errorMessage} />}
            {isLoading && (
              <div className="w-full flex justify-center items-center px-8 pt-8">
                <ProgressBar progress={loadingProgress} subtitle={loadingTitle()} />
              </div>
            )}
            {userData?.plan?.unconfirmed && (
              <div className="mb-4 bg-green-100 rounded p-4 text-sm flex justify-between items-center">
                <span className="text-green-700">{`Your plan has been upgraded to ${userData.plan.name}! Thanks for choosing Letter Atlas 🙂`}</span>
                <X className="text-green-500 cursor-pointer mr-2" size={16} onClick={confirmPlanUpgrade} />
              </div>
            )}
            {children ? children : userData &&
              userSummaries &&
              userSummaries.length > 0
              ? renderMainContent()
              : !isLoading && !isOnboarding && !errorMessage && (
                <>
                  <div className="text-center text-gray-500 mt-8">
                    Your {userData?.plan.name === "free" ? "weekly" : "daily"} summaries will show here.
                  </div>
                  <div className="text-center text-xs">
                    Select some categories and/or newsletters to get started!
                  </div>
                </>
              )}
            {isOnboarding && <Onboarding onComplete={() => setIsOnboarding(false)} />}
          </div>
        </main>
        
        <aside
          className={`border-l border-gray-200 overflow-auto transition-all duration-300 ease-in-out transform flex flex-col
            md:relative md:drawer-desktop
            ${isContextDrawerOpen 
              ? `opacity-100 drawer-open translate-x-0 fixed ${window.innerWidth < 768 ? "top-[57px]" : "top-0"} bottom-0 right-0 z-50 bg-white` 
              : "opacity-0 w-0 translate-x-full"}`}
        >
          <RightSidebar
            toggleSection={toggleSection}
            timeSaved={userData?.time_saved || 0}
            userData={userData}
            annotations={annotations}
            onCloseAnnotations={handleCloseAnnotations}
            setIsContextDrawerOpen={setIsContextDrawerOpen}
          />
        </aside>
        {isContextDrawerOpen && (
          <div 
            className={`md:hidden fixed inset-0 ${window.innerWidth < 768 ? "top-[57px]" : "top-0"} bg-black bg-opacity-50 z-40`}
            onClick={() => setIsContextDrawerOpen(false)}
          />
        )}
      </div>
      
      {/* Audio Player */}
      {audio.url && (
        <AudioPlayer
          audio={audio}
          setAudio={setAudio}
        />
      )}
    </div>
  );
};

export default HomeWrapper;
